import React, {
  useState, useEffect, useRef, useCallback,
} from 'react';
import PropTypes from 'prop-types';
import { makeStyles, CircularProgress } from '@material-ui/core';

import Select from './Select';
import OptionsList from './OptionsList';
import useOutsideClick from './useOutsideClick';

const useStyles = makeStyles(() => ({
  loadingBox: {
    position: 'absolute',
    top: '120%',
    right: 0,
    backgroundColor: '#fff',
    boxShadow: '0px 0px 4px rgba(0, 0, 0, 0.25)',
    borderRadius: '4px',
    width: '100%',
    height: '100px',
    cursor: 'pointer',
    zIndex: 9999,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
}));

const OptionSelect = ({
  options,
  onUpdate,
  isMultiSelect,
  isLoading,
  defaultEmptySelectListText,
  selectTitle,
  alignOptionsToLeft,
}) => {
  const classes = useStyles();
  const [optionsWithStatus, setOptionsWithStatus] = useState([]);

  const [isOpen, setIsOpen] = useState(false);

  const containerRef = useRef();
  useOutsideClick(containerRef, () => {
    if (isOpen) {
      setIsOpen(false);
    }
  });

  const toggleOptions = useCallback(() => setIsOpen((prev) => !prev), []);

  useEffect(() => {
    setOptionsWithStatus(
      options.map(({ id, title, isSelected }) => ({ id, title, isSelected: isSelected || false })),
    );
  }, [isLoading]);

  const onSelect = (selectedId) => {
    setOptionsWithStatus(
      (prevOptions) => {
        const newOptions = prevOptions.map((option) => {
          if (selectedId === 'all') {
            return {
              ...option,
              isSelected: false,
            };
          }

          if (selectedId === option.id) {
            return {
              ...option,
              isSelected: !option.isSelected,
            };
          }

          if (!isMultiSelect) {
            return {
              ...option,
              isSelected: false,
            };
          }

          return option;
        });

        if (!isMultiSelect) {
          setIsOpen(false);
        }

        const selectedOptions = newOptions
          .filter(({ isSelected }) => isSelected)
          .map(({ id, title }) => ({ id, title }));

        onUpdate(
          isMultiSelect ? selectedOptions : selectedOptions[0],
        );

        return newOptions;
      },
    );
  };

  const selectedOptions = optionsWithStatus.filter(({ isSelected }) => isSelected);

  const firstThreeActiveOptionsList = selectedOptions.slice(0, 3).map(({ title }) => title).join(', ');
  const selectedItemsText = selectedOptions.length <= 3 ? firstThreeActiveOptionsList : `${firstThreeActiveOptionsList} +${selectedOptions.length - 3}`;

  return (
    <Select
      title={selectTitle}
      selectedItems={selectedOptions.length > 0 ? selectedItemsText : defaultEmptySelectListText}
      onToggle={toggleOptions}
      isOpen={isOpen}
      optionsListRef={containerRef}
    >
      {isLoading && (
        <div className={classes.loadingBox}>
          <CircularProgress />
        </div>
      )}
      {!isLoading && (
        <OptionsList
          alignToLeft={alignOptionsToLeft}
          options={optionsWithStatus}
          onSelect={onSelect}
          isMultiSelect={isMultiSelect}
          isAll={selectedOptions.length === 0}
          allLabel={defaultEmptySelectListText}
        />
      )}
    </Select>
  );
};

OptionSelect.propTypes = {
  options: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    title: PropTypes.string,
  })).isRequired,
  onUpdate: PropTypes.func,
  isMultiSelect: PropTypes.bool,
  isLoading: PropTypes.bool,
  defaultEmptySelectListText: PropTypes.string,
  selectTitle: PropTypes.string.isRequired,
  alignOptionsToLeft: PropTypes.bool,
};

OptionSelect.defaultProps = {
  onUpdate: () => {},
  isMultiSelect: false,
  isLoading: false,
  defaultEmptySelectListText: 'All',
  alignOptionsToLeft: false,
};

export default OptionSelect;
